import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { X, CheckCircle, XCircle, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

function QuoteRequestResponseModal({ quoteRequest, onClose }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(null);
  const [siteVisitScheduledAt, setSiteVisitScheduledAt] = useState('');
  const [responseNotes, setResponseNotes] = useState('');

  const updateMutation = useMutation({
    mutationFn: async () => {
      const payload = { status };
      if (status === 'ACCEPTED' && siteVisitScheduledAt) {
        payload.site_visit_scheduled_at = siteVisitScheduledAt;
      }
      if (responseNotes) payload.response_notes = responseNotes;
      const res = await api.patch(`/quote-requests/${quoteRequest.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Response recorded');
      queryClient.invalidateQueries(['quote-requests', 'mine']);
      queryClient.invalidateQueries(['repairs']);
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to update');
    },
  });

  const handleSubmit = () => {
    if (!status) {
      toast.error('Select Accept or Reject');
      return;
    }
    updateMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Respond to Quote Request</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {quoteRequest.building_name} · {quoteRequest.ss_code} {quoteRequest.ss_name}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Response</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStatus('ACCEPTED')}
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  status === 'ACCEPTED' ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-300'
                }`}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Accept
              </button>
              <button
                type="button"
                onClick={() => setStatus('REJECTED')}
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  status === 'REJECTED' ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-300'
                }`}
              >
                <XCircle className="w-5 h-5 mr-2" />
                Reject
              </button>
            </div>
          </div>
          {status === 'ACCEPTED' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site visit date (optional)</label>
              <input
                type="datetime-local"
                value={siteVisitScheduledAt}
                onChange={(e) => setSiteVisitScheduledAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={responseNotes}
              onChange={(e) => setResponseNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g. Will visit on Tuesday"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateMutation.isLoading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {updateMutation.isLoading ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuoteRequestResponseModal;
